import React from 'react';
import { useConnectionAlerts } from '@/hooks/useConnectionAlerts';
import { WifiOff, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";

interface ConnectionGuardProps {
  children: React.ReactNode;
}

export const ConnectionGuard: React.FC<ConnectionGuardProps> = ({ children }) => {
  const { t } = useTranslation("common");
  const { whatsappDisconnected, loading } = useConnectionAlerts();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="w-full h-[calc(100vh-120px)] flex items-center justify-center bg-[#F8FAFC] transition-colors duration-200 dark:bg-[#18181B]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]" />
          <p className="text-sm text-[#64748B] dark:text-[#A1A1AA]">
            {t("shared.checkingWhatsApp")}
          </p>
        </div>
      </div>
    );
  }

  if (whatsappDisconnected) {
    return (
      <div className="w-full h-[calc(100vh-120px)] flex items-center justify-center bg-[#F8FAFC] transition-colors duration-200 dark:bg-[#18181B]">
        <div className="max-w-md w-full mx-4">
          <div className="rounded-2xl border border-[#E5E7EB] bg-white p-8 text-center shadow-sm transition-colors duration-200 dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <div className="flex justify-center mb-6">
              <div className="flex h-20 w-20 items-center justify-center rounded-full border border-amber-200 bg-[#FFFBEB] dark:border-amber-500/20 dark:bg-amber-500/10">
                <WifiOff className="h-10 w-10 text-[#D97706] dark:text-amber-400" />
              </div>
            </div>

            <h1 className="mb-3 text-2xl font-bold tracking-tight text-[#0F172A] dark:text-[#FAFAFA]">
              {t("shared.whatsappDisconnected")}
            </h1>

            <p className="mb-2 text-[#64748B] dark:text-[#A1A1AA]">
              {t("shared.chatUnavailable")}
            </p>

            <p className="mb-8 text-sm text-[#94A3B8] dark:text-[#71717A]">
              {t("shared.connectToUseChat")}
            </p>

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => navigate('/settings')}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2563EB] px-4 py-3 font-medium text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8]"
              >
                <Settings className="h-4 w-4" />
                {t("shared.connectWhatsApp")}
              </button>

              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 font-medium text-[#334155] transition-colors duration-200 hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
              >
                {t("shared.backToDashboard")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
